import React, { useState } from "react";
import PropTypes from "prop-types";
import clsx from "classnames";

import { makeStyles } from "@material-ui/core/styles";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardActions from "@material-ui/core/CardActions";
import Grid from "@material-ui/core/Grid";
import Divider from "@material-ui/core/Divider";

import { SimpleShowLayout, WithReduxForm, TextInput, ReferenceInput, AutoCompleteInput, TextField, Button, translate } from "jazasoft";

import CardHeader from "../../../components/CardHeader";
import CustomInputElements from "../../../components/CustomInputElements";

import { fieldOptions } from "../../../utils/helpers";
import { OrderFields } from "../../../utils/entityFields";
import getValidation from "../../../utils/getValidation";
import hasPrivilege from "../../../utils/hasPrivilege";
import ResourceId from "../../../utils/ResourceId";
import { Scope } from "../../../utils/privilege";

const InputElements = ({ fields = [], i18nKey, validationRule = {}, isAdmin, isFPOCreated, isCutPlanInProgress, isClosed }) => {
  const customFields = fields?.filter(f => f.startsWith("customField")) || [];

  if (isClosed) {
    const emptyRule = [...fields];
    validationRule = Object.keys(validationRule).reduce(
      (acc, key) => ({ ...acc, [key]: { ...validationRule[key], disabled: customFields.includes(key) } }),
      emptyRule
    );
  }

  return (
    <>
      {fields.includes(OrderFields.PO_REF) && (
        <Grid item xs={12} sm={3}>
          <TextInput
            i18nKey={i18nKey}
            source="poRef"
            disabled={isAdmin ? isCutPlanInProgress : isFPOCreated}
            validate={getValidation(validationRule[OrderFields.PO_REF])}
            fullWidth
          />
        </Grid>
      )}
      {fields.includes(OrderFields.CUSTOMER_PO_REF) && (
        <Grid item xs={12} sm={3}>
          <TextInput
            i18nKey={i18nKey}
            source="customerPoRef"
            disabled={isClosed}
            validate={getValidation(validationRule[OrderFields.CUSTOMER_PO_REF])}
            fullWidth
          />
        </Grid>
      )}

      {fields.includes(OrderFields.CUSTOMER_ID) && (
        <Grid item xs={12} sm={3}>
          <ReferenceInput
            i18nKey={i18nKey}
            source="customerId"
            reference="customers"
            disabled={isAdmin ? isCutPlanInProgress : isFPOCreated}
            validate={getValidation(validationRule[OrderFields.CUSTOMER_ID])}>
            <AutoCompleteInput options={{ fullWidth: true }} />
          </ReferenceInput>
        </Grid>
      )}

      {fields.includes(OrderFields.SEASON_ID) && (
        <Grid item xs={12} sm={3}>
          <ReferenceInput
            i18nKey={i18nKey}
            source="seasonId"
            reference="seasons"
            disabled={isClosed}
            validate={getValidation(validationRule[OrderFields.SEASON_ID])}>
            <AutoCompleteInput options={{ fullWidth: true }} />
          </ReferenceInput>
        </Grid>
      )}

      {fields.includes(OrderFields.MERCHANT) && (
        <Grid item xs={12} sm={3}>
          <ReferenceInput
            label="Merchant"
            source="merchant"
            reference="users"
            searchKeys={["fullName", "username"]}
            pageSize={100}
            disabled={isCutPlanInProgress}
            validate={getValidation(validationRule[OrderFields.MERCHANT])}
            idKey="fullName"
            options={{ fullWidth: true }}>
            <AutoCompleteInput optionText={record => `${record.fullName} (${record.username || ""})`} optionValue="fullName" disablePortal={true} />
          </ReferenceInput>
        </Grid>
      )}

      <CustomInputElements fields={customFields} validationRule={validationRule} i18nKey={i18nKey} />

      {fields.includes(OrderFields.DESCRIPTION) && (
        <Grid item xs={12} sm={3}>
          <TextInput
            i18nKey={i18nKey}
            source="desc"
            disabled={isClosed}
            validate={getValidation(validationRule[OrderFields.DESCRIPTION])}
            fullWidth
          />
        </Grid>
      )}
    </>
  );
};

const useStyles = makeStyles({
  root: {},
  footer: {
    padding: "1em",
    display: "flex",
    justifyContent: "flex-end",
    "& > *": {
      marginLeft: "1em"
    }
  }
});

const BasicDetails = props => {
  const {
    fields = [],
    i18nKey,
    validationRule = {},
    saving,
    record,
    onSubmit,
    isAdmin,
    isFabricAssociated,
    isCutPlanInProgress,
    isFPOCreated,
    isClosed,
    translate,
    roles,
    hasAccess
  } = props;
  const classes = useStyles();

  const withForm = typeof onSubmit === "function";
  const [editing, setEditing] = useState(props.editing);

  const onSubmitClick = handleSubmit => {
    handleSubmit(values => {
      onSubmit(values, () => setEditing(false));
    })();
  };

  const customFields = fields.filter(field => field.startsWith("customField"));

  const title = translate(props.title, { _: props.title });

  if (editing) {
    if (withForm) {
      return (
        <div className={clsx(classes.root, props.className)}>
          <WithReduxForm initialValues={record}>
            {({ handleSubmit }) => (
              <div>
                <Card>
                  <CardHeader title={title} />
                  <Divider />
                  <CardContent>
                    <Grid container spacing={2}>
                      <InputElements
                        fields={fields}
                        i18nKey={i18nKey}
                        validationRule={validationRule}
                        isAdmin={isAdmin}
                        isFabricAssociated={isFabricAssociated}
                        isFPOCreated={isFPOCreated}
                        isCutPlanInProgress={isCutPlanInProgress}
                        isClosed={isClosed}
                      />
                    </Grid>
                  </CardContent>

                  <Divider />
                  {/** Footer */}
                  <div className={classes.footer}>
                    <Button
                      label={translate("js.action.cancel", { _: "Cancel" })}
                      variant="outlined"
                      color="primary"
                      onClick={() => setEditing(false)}
                    />
                    <Button
                      style={{ marginLeft: "1em" }}
                      label={translate("js.action.save", { _: "Save" })}
                      variant="contained"
                      color="primary"
                      disabled={saving}
                      onClick={() => onSubmitClick(handleSubmit)}
                    />
                  </div>
                </Card>
              </div>
            )}
          </WithReduxForm>
        </div>
      );
    } else {
      return (
        <div className={clsx(classes.root, props.className)}>
          <Card>
            <CardHeader title={title} />
            <Divider />
            <CardContent>
              <Grid container spacing={2}>
                <InputElements
                  fields={fields}
                  i18nKey={i18nKey}
                  validationRule={validationRule}
                  isAdmin={isAdmin}
                  isFabricAssociated={isFabricAssociated}
                  isFPOCreated={isFPOCreated}
                  isCutPlanInProgress={isCutPlanInProgress}
                  isClosed={isClosed}
                />
              </Grid>
            </CardContent>
          </Card>
        </div>
      );
    }
  } else {
    return (
      <Card className={clsx(classes.root, props.className)}>
        <CardHeader title={title} />
        <Divider />
        <SimpleShowLayout style={{ padding: "1.5em" }} footer={false} record={record} i18nKey={i18nKey}>
          {fields.includes(OrderFields.PO_REF) && <TextField source="poRef" {...fieldOptions(3)} />}
          {fields.includes(OrderFields.CUSTOMER_PO_REF) && <TextField source="customerPoRef" {...fieldOptions(3)} />}
          {fields.includes(OrderFields.CUSTOMER_ID) && <TextField source="customer.name" {...fieldOptions(3)} />}
          {fields.includes(OrderFields.SEASON_ID) && <TextField source="season.name" {...fieldOptions(3)} />}
          {fields.includes(OrderFields.TOTAL_QTY) && <TextField source="totalQty" {...fieldOptions(3)} />}
          {customFields.map((field, idx) => (
            <TextField key={idx} source={field} {...fieldOptions(3)} />
          ))}
          {fields.includes(OrderFields.DESCRIPTION) && <TextField source="desc" {...fieldOptions(3)} />}
        </SimpleShowLayout>
        {withForm && !isClosed && hasPrivilege(roles, hasAccess, ResourceId.ORDER, Scope.UPDATE) && <Divider />}
        {withForm && !isClosed && hasPrivilege(roles, hasAccess, ResourceId.ORDER, Scope.UPDATE) && (
          <CardActions style={{ padding: "1em" }}>
            <Button
              style={{ marginLeft: "auto" }}
              label="js.action.edit"
              variant="outlined"
              color="primary"
              disabled={saving}
              onClick={() => setEditing(true)}
            />
          </CardActions>
        )}
      </Card>
    );
  }
};

BasicDetails.propTypes = {
  title: PropTypes.string,
  fields: PropTypes.arrayOf(PropTypes.string).isRequired,
  validationRule: PropTypes.object.isRequired,
  i18nKey: PropTypes.string,
  record: PropTypes.object,
  onSubmit: PropTypes.func,
  editing: PropTypes.bool,
  saving: PropTypes.bool
};

BasicDetails.defaultProps = {
  title: "Basic Details"
};

export default translate(BasicDetails);
